import { FC } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
// components
import { Alert } from 'components/common/Alert';
import { BackdropLoader } from 'components/common/BackdropLoader';
import IngredientForm from 'components/main/Ingredients/Add/IngredientForm';
// constants, styles, interfaces, schema, reducer, context
import { formatIngredientInput } from 'lib/helper';
import { addNewIngredientSchema } from 'validationSchema';
import { useCreateIngredientMutation } from 'generated/graphql';
import { AddNewIngredientFormType, AddIngredientModalProps } from 'interfaces';
import {
  CANCEL_TEXT,
  HTTP_STATUS,
  ADD_INGREDIENT_TEXT,
  INGREDIENT_IS_CREATING_TEXT,
  addNewIngredientInitialValues,
} from 'constants/index';

const AddIngredientModal: FC<AddIngredientModalProps> = ({ open, onClose }) => {
  const methods = useForm<AddNewIngredientFormType>({
    defaultValues: addNewIngredientInitialValues,
    resolver: yupResolver(addNewIngredientSchema),
  });

  const { handleSubmit } = methods;

  const [createIngredient, { loading: createIngredientLoading }] = useCreateIngredientMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { createIngredient } = data;
      const { response } = createIngredient || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.CREATED) {
        Alert.success(message || '');
        onClose();
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const onSubmit: SubmitHandler<AddNewIngredientFormType> = async (data) => {
    const createIngredientInput = formatIngredientInput(data);
    await createIngredient({
      variables: {
        createIngredientInput,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="add-ingredient-dialog-title"
      aria-describedby="add-ingredient-dialog-description">
      <DialogTitle id="add-ingredient-dialog-title">
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          {ADD_INGREDIENT_TEXT}
        </Typography>
      </DialogTitle>

      <DialogContent id="add-ingredient-dialog-description">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} id="add-ingredient-form">
            <IngredientForm loading={createIngredientLoading} contained />
          </form>
        </FormProvider>
        <BackdropLoader open={createIngredientLoading} text={INGREDIENT_IS_CREATING_TEXT} />
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
        <Button onClick={onClose} variant="outlined">
          {CANCEL_TEXT}
        </Button>
        <Button
          type="submit"
          form="add-ingredient-form"
          color="primary"
          variant="contained"
          id="create-ingredient-btn"
          disabled={createIngredientLoading}>
          {ADD_INGREDIENT_TEXT}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddIngredientModal;
